import { ComponentChild, h, render, TargetedKeyboardEvent } from 'preact';
import uuid from '../../../utils/uuid';
import AdyenCheckoutError from '../../../core/Errors/AdyenCheckoutError';
import { NO_CHECKOUT_ATTEMPT_ID } from '../../../core/Analytics/constants';
import type { ICore } from '../../../core/types';
import type { BaseElementProps, BaseElementState, IBaseElement } from './types';
import type { PaymentData } from '../../../types/global-types';
import { off, on } from '../../../utils/listenerUtils';
import { AbstractAnalyticsEvent } from '../../../core/Analytics/events/AbstractAnalyticsEvent';
import { createSdkData } from '../../../utils/createSdkData';
import getComponentNameOfPaymentType from '../../components-name-map';
import { PAYMENT_METHOD_BEHAVIOR } from '../../../core/config';

/**
 * Verify if the first parameter is instance of Core.
 * We do not use 'instanceof' to avoid importing the Core class directly into this class.
 * @param checkout
 */
function assertIsCoreInstance(checkout: ICore): checkout is ICore {
    if (!checkout) return false;
    const isCoreObject = typeof checkout.initialize === 'function' && typeof checkout.createFromAction === 'function';
    return isCoreObject;
}

abstract class BaseElement<P extends BaseElementProps, S extends BaseElementState = BaseElementState> implements IBaseElement<P> {
    public readonly _id = `${this.constructor['type']}-${uuid()}`;
    public readonly core: ICore;

    public props: P;
    public state: S = {} as S;
    public _component: ComponentChild | undefined;

    protected _node: HTMLElement = null;

    protected static readonly defaultProps = {};

    constructor(checkout: ICore, props?: P) {
        const isCoreInstance = assertIsCoreInstance(checkout);

        if (!isCoreInstance) {
            throw new AdyenCheckoutError(
                'IMPLEMENTATION_ERROR',
                `Trying to initialise the component '${this.constructor['type']}' without a reference to an instance of AdyenCheckout`
            );
        }

        this.core = checkout;
        this.buildElementProps(props);

        this.handleKeyDown = this.handleKeyDown.bind(this);
    }

    protected buildElementProps(componentProps?: P) {
        this.props = this.formatProps({ ...this.constructor['defaultProps'], ...componentProps });
    }

    /**
     * Executed during creation of any payment element.
     * Gives a chance to any paymentMethod to format the props we're receiving.
     */
    protected formatProps(props: P) {
        return props;
    }

    /**
     * Executed on the `data` getter.
     * Returns the component data necessary for the /payments request
     */
    protected formatData(): PaymentData {
        return {} as PaymentData;
    }

    protected submitAnalytics(_analyticsObj?: AbstractAnalyticsEvent) {
        return null;
    }

    protected handleKeyDown(_e: TargetedKeyboardEvent<HTMLInputElement>) {
        return null;
    }

    protected setState(newState: S): void {
        this.state = { ...this.state, ...newState };
    }

    /**
     * Returns the component payment data ready to submit to the Checkout API
     * Note: this does not ensure validity, check isValid first
     */
    public get data(): PaymentData {
        const order = this.state.order || this.props.order;
        const componentData = this.formatData();
        const doesPaymentMethodHaveNativeComponent = Boolean(getComponentNameOfPaymentType(componentData.paymentMethod?.type));
        const clientData = this.core.modules.risk.riskData;
        const checkoutAttemptId = this.core.modules.analytics.checkoutAttemptId ?? NO_CHECKOUT_ATTEMPT_ID;
        const paymentMethodBehavior = doesPaymentMethodHaveNativeComponent ? PAYMENT_METHOD_BEHAVIOR.NATIVE : PAYMENT_METHOD_BEHAVIOR.GENERIC;

        const sdkData = createSdkData(checkoutAttemptId, clientData, paymentMethodBehavior);
        if (componentData.paymentMethod && checkoutAttemptId) {
            componentData.paymentMethod.checkoutAttemptId = checkoutAttemptId;
        }

        if (componentData.paymentMethod && sdkData) {
            componentData.paymentMethod.sdkData = sdkData;
        }

        return {
            ...(clientData && { riskData: { clientData } }),
            ...(order && { order: { orderData: order.orderData, pspReference: order.pspReference } }),
            ...componentData,
            clientStateDataIndicator: true
        };
    }

    /**
     * Method used to make the payment method active
     * (Useful when there are different payment methods available and activating one PM must trigger a specific task)
     */
    public activate(): void {
        return;
    }

    public render(): ComponentChild | Error {
        // render() not implemented in the element
        throw new Error('Payment method cannot be rendered.');
    }

    /**
     * Mounts an element into the dom
     * @param domNode - Node (or selector) where we will mount the payment element
     * @returns this - the payment element instance we mounted
     */
    public mount(domNode: HTMLElement | string): this {
        const node = typeof domNode === 'string' ? document.querySelector<HTMLElement>(domNode) : domNode;

        if (!node) {
            throw new Error('Component could not mount. Root node was not found.');
        }

        if (this._node) {
            this.unmount(); // new, if this._node exists then we are "remounting" so we first need to unmount if it's not already been done
        }

        this._node = node;

        // Add listener for key down events, notably 'Enter' key presses
        on(this._node, 'keydown', this.handleKeyDown, false);

        this._component = this.render();

        render(this._component, node);

        return this;
    }

    /**
     * Updates props, resets the internal state and remounts the element.
     *
     * @param props - props to update
     * @returns this - the element instance
     */
    public update(props: Partial<P>): this {
        this.props = this.formatProps({ ...this.props, ...props });
        this.state = {} as S;

        return this.unmount().mount(this._node); // for new mount fny
    }

    /**
     * Unmounts a payment element from the DOM
     */
    public unmount(): this {
        // Remove listener
        off(this._node, 'keydown', this.handleKeyDown);

        if (this._node) {
            render(null, this._node);
        }

        return this;
    }

    /**
     * Unmounts an element and removes it from the parent instance
     * For "destroy" type cleanup - when you don't intend to use the component again
     */
    public remove() {
        this.unmount();

        if (this.core) {
            this.core.remove(this);
        }
    }
}

export default BaseElement;
